import { Controller } from "@hotwired/stimulus";
import { toastManager } from "../components/DownloadTray/ToastManager";

/**
 * Bulk Invitees Controller (GR-1973)
 *
 * Thin plumbing for the "Add multiple by email" box on event-request wizard
 * Step 7. All real work happens server-side:
 *   - #review POSTs the pasted text to parse_invitees, which returns an
 *     editable review grid (emails parsed/validated/deduped, names prefilled
 *     from Azure AD or the email address).
 *   - #add gathers the (possibly edited) grid rows and POSTs them to
 *     add_invitees, which creates the invitees and redirects back to Step 7.
 *
 * This lives inside the wizard <form>, so it uses fetch rather than nested
 * <form> elements.
 */
export default class extends Controller {
  static targets = ["emails", "review", "reviewGrid", "reviewHeading", "addLabel"];
  static values = { parseUrl: String, addUrl: String };

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || "";
  }

  async review() {
    const text = this.emailsTarget.value.trim();
    if (!text) {
      this.emailsTarget.focus();
      return;
    }

    this.clearError();

    const body = new FormData();
    body.append("bulk_invitee_emails", text);

    let response;
    try {
      response = await fetch(this.parseUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": this.csrfToken, Accept: "text/html" },
        body,
      });
    } catch {
      this.showError("Couldn't reach the server. Please try again.");
      return;
    }

    if (!response.ok) {
      this.showError("Sorry, we couldn't process those emails. Please try again.");
      return;
    }

    this.reviewTarget.innerHTML = await response.text();
  }

  // Drop a single row from the review grid before adding. Removed rows are no
  // longer in the DOM, so #add naturally excludes them.
  removeRow(event) {
    event.target.closest(".invitee-review-row")?.remove();
    this.refreshCount();
  }

  // Keep the heading + Add button count in sync with the remaining rows. When
  // the last row is removed, drop the grid entirely.
  refreshCount() {
    const remaining = this.hasReviewGridTarget
      ? this.reviewGridTarget.querySelectorAll(".invitee-review-row").length
      : 0;

    if (remaining === 0) {
      this.reviewTarget.innerHTML = "";
      return;
    }

    const label = `${remaining} ${remaining === 1 ? "invitee" : "invitees"}`;
    if (this.hasReviewHeadingTarget) this.reviewHeadingTarget.textContent = `Review ${label}`;
    if (this.hasAddLabelTarget) this.addLabelTarget.textContent = label;
  }

  // Discard the parsed review grid without adding anyone, and reset the box so
  // the requester can paste a fresh list.
  clear() {
    this.reviewTarget.innerHTML = "";
    this.emailsTarget.value = "";
    this.emailsTarget.focus();
  }

  async add(event) {
    event.preventDefault();
    if (!this.hasReviewGridTarget) return;

    this.clearError();

    const rows = Array.from(this.reviewGridTarget.querySelectorAll(".invitee-review-row"));

    // Require a first + last name on every row before submitting, so the server
    // doesn't silently reject rows whose name couldn't be resolved (e.g. a
    // single-token email guess leaves the last name blank).
    const incomplete = rows.find((row) => {
      const first = row.querySelector('[data-review-field="first_name"]')?.value.trim();
      const last = row.querySelector('[data-review-field="last_name"]')?.value.trim();
      return !first || !last;
    });
    if (incomplete) {
      const firstField = incomplete.querySelector('[data-review-field="first_name"]');
      const lastField = incomplete.querySelector('[data-review-field="last_name"]');
      (firstField?.value.trim() ? lastField : firstField)?.focus();
      this.showError("Add a first and last name for every invitee before adding them.");
      return;
    }

    const body = new FormData();
    rows.forEach((row, i) => {
      body.append(`invitees[${i}][email]`, row.dataset.email);
      body.append(`invitees[${i}][first_name]`, row.querySelector('[data-review-field="first_name"]').value);
      body.append(`invitees[${i}][last_name]`, row.querySelector('[data-review-field="last_name"]').value);
    });
    // GR-2566: the appended rows must use numeric nested-attributes indices
    // continuing the current DOM sequence, or strong params drops them. Send the
    // next free index so the server renders [N], [N+1], ...
    body.append("start_index", this.nextInviteeIndex());

    let response;
    try {
      response = await fetch(this.addUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": this.csrfToken, Accept: "application/json" },
        body,
      });
    } catch {
      this.showError("Couldn't reach the server. Please try again.");
      return;
    }

    if (!response.ok) {
      this.showError("Sorry, we couldn't add those invitees. Please try again.");
      return;
    }

    // GR-2566: add_invitees persists the rows and returns their HTML. Append
    // them in place instead of reloading the page. Guard the JSON parse: a
    // followed redirect (e.g. session expiry -> login HTML) is response.ok but
    // not JSON, so fall back to an error rather than silently no-op'ing.
    let data;
    try {
      data = await response.json();
    } catch {
      this.showError("Sorry, we couldn't add those invitees. Please try again.");
      return;
    }
    this.applyAdded(data);
  }

  // Next free nested-attributes index across all invitee rows (server-rendered,
  // JS-added, and previously bulk-appended). Mirrors invitees_controller#getNextIndex.
  nextInviteeIndex() {
    const inputs = document.querySelectorAll(
      'input[name^="event_request[event_request_invitees_attributes]"]'
    );
    let max = -1;
    inputs.forEach((input) => {
      const match = input.name.match(/\[(\d+)\]/);
      if (match) max = Math.max(max, parseInt(match[1], 10));
    });
    return max + 1;
  }

  applyAdded(data) {
    const list = document.querySelector(".invitees-list");
    if (list && data.rows_html) {
      list.querySelector(".blankslate, .Blankslate")?.remove();
      list.insertAdjacentHTML("beforeend", data.rows_html);
    }

    // Reset the bulk box for the next paste.
    this.reviewTarget.innerHTML = "";
    this.emailsTarget.value = "";

    // Ask the invitees controller to refresh its counter / empty state.
    document
      .getElementById("invitees-container")
      ?.dispatchEvent(new CustomEvent("invitees:refresh", { bubbles: true }));

    if (data.failed_message) this.showError(data.failed_message);

    if (data.added > 0) {
      toastManager.show({
        message: `${data.added} ${data.added === 1 ? "invitee" : "invitees"} added.`,
        variant: "success",
        icon: "check",
        autoCloseMs: 3000,
      });
    }
  }

  showError(message) {
    let el = this.element.querySelector(".bulk-invitees-error");
    if (!el) {
      el = document.createElement("div");
      el.className = "bulk-invitees-error flash flash-error mt-2";
      el.setAttribute("role", "alert");
      this.element.appendChild(el);
    }
    el.textContent = message;
  }

  clearError() {
    this.element.querySelector(".bulk-invitees-error")?.remove();
  }
}
